// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import { Button, Row } from "react-bootstrap";
import logo from "../images/perun_logo.png";
import ethlogo from "../images/assets/ETH_logo.png";
import { useTranslation } from "react-i18next";
import { dispatchPerunError } from "../events/Error";
import Provider, {
  CkbCCCProvider,
  FreighterProvider,
  Web3Provider,
} from "../client/Provider";
import { Connection } from "../wire/connection";
import { ethers } from "ethers";
import config from "../config.json";
import { AppContext } from "../AppContext";
import { onboardMM } from "../web3/web3";
import { ethNetworkName } from "../constants/Constants";
import { Client } from "../client/Client";
import connected from "../images/symbols/connected.png";
import notConnected from "../images/symbols/not_connected.png";
import ChainsList from "./ChainsList";
import { dispatchPerunNotice, Instructions } from "../events/Notice";
import { CKBContext } from "./CKBProvider";
import { BiX } from "react-icons/bi";
import ckblogo from "../images/assets/CKB_logo.png";
import { ClientJsonRpc } from "@ckb-ccc/core";
import { ckbNetworkName, newCKBClient } from "../constants/CKBNetwork";
import Address from "../api/types/address";

interface Props {
  toggleOnboarded: () => void;
}

export function Onboarding_combined(props: Props) {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [connection, setConnection] = useState(false);
  const [ethereumSelected, setEthereumSelected] = useState(false);
  const [nervosSelected, setNervosSelected] = useState(false);
  const [ckbAvailable, setCkbAvailable] = useState<boolean | null>(null);
  const [ckbChecking, setCkbChecking] = useState(false);
  const [mmProvider, setMmProvider] =
    useState<ethers.providers.Web3Provider | null>(null);
  const [mmAccount, setMmAccount] = useState<string | null>(null);
  const { signer, address, open } = useContext(CKBContext);

  useEffect(() => {
    console.log(isConnecting, isProcessing, connection);
    // Check if the user has visited the page before
    const hasVisited = localStorage.getItem("hasVisited");

    if (!hasVisited) {
      dispatchPerunNotice(Instructions());
      localStorage.setItem("hasVisited", "true"); // Mark the user as having visited
    }
  }, []);

  async function ensureMMOnce() {
    console.log("Ensuring MM...");
    if (mmProvider !== null && mmAccount !== null) {
      console.log("MM already onboarded");
      return { provider: mmProvider, account: mmAccount };
    }
    const networkIDs = config.networkIDs.map((id) => BigInt(id));
    console.log("Network IDs: ", networkIDs);
    const res = await onboardMM(networkIDs);
    console.log("Onboarded MM: ", res);
    setMmProvider(res.provider);
    setMmAccount(res.account);
    return res;
  }

  const checkCkbAvailability = async (): Promise<boolean> => {
    // Avoid overlapping checks; if one is in progress just reuse the last value
    if (ckbChecking) {
      return ckbAvailable ?? false;
    }

    setCkbChecking(true);
    try {
      const client: ClientJsonRpc = newCKBClient();
      await client.getTip()

      setCkbAvailable(true);
      return true;
    } catch (e) {
      console.error("CKB RPC health check failed", e);
      setCkbAvailable(false);
      return false;
    } finally {
      setCkbChecking(false);
    }
  };

  const handleProcessChange = (isProcessing: boolean) => {
    setIsProcessing(isProcessing);
  };

  const handleConnection = (connecting: boolean) => {
    console.log("Connection status: ", connecting);
    setConnection(connecting);
  };

  const saveClientState = (client: Client) => {
    const state = {
      ethAddress: client.ethAddress? Address.toJSON(client.ethAddress): undefined,
      steAddress: client.steAddress,
      nervosAddress: client.nervosAddress,
      nervosPubKey: client.nervosPubKey,
      mmAccount: mmAccount,
      l2Account: client.l2Address ? Address.toJSON(client.l2Address) : undefined,
    };
    console.log("Saving state: ", state)
    localStorage.setItem("clientState", JSON.stringify(state));
  };

  const initClient = async (
    ethProvider?: Provider,
    steProvider?: FreighterProvider,
    nervosProvider?: CkbCCCProvider,
  ) => {
    const conn = new Connection(
      new URL(`${config.wsAddress}:${config.wsPort + config.wsPath}`),
    );
    let pubKey: string | undefined;
    let nervosAddr: string | undefined;
    if (nervosSelected && nervosProvider) {
      nervosAddr = await nervosProvider?.getInternalAddress();
      console.log("----------Address:", nervosAddr);
      const key = "nervospubkey_of_" + nervosAddr;
      let nervosPubKey = localStorage.getItem(key);
      if (nervosPubKey === null) {
        const nervosPubKeyBytes = await nervosProvider?.getPublicKey();
        if (nervosPubKeyBytes) {
          localStorage.setItem(
            key,
            btoa(String.fromCharCode(...nervosPubKeyBytes)),
          );
          nervosPubKey = btoa(String.fromCharCode(...nervosPubKeyBytes!));
          pubKey = nervosPubKey;
        }
      } else {
        pubKey = nervosPubKey;
      }
    }
    if (
      ethProvider == undefined &&
      nervosProvider == undefined &&
      steProvider == undefined
    ) {
      dispatchPerunError(<p>{t("NoProviderConnected")}</p>);
      setConnection(false);
      setIsProcessing(false);
      localStorage.removeItem("nervospubkey");
      return;
    }
    try {
      const client = new Client(
        conn,
        ethProvider,
        steProvider,
        nervosProvider,
        mmAccount!,
        "",
        nervosAddr,
        pubKey,
      );
      setIsConnecting(true);

      const isSuccess = await client
        .initializeConn()
        .then((success) => {
          if (success) {
            console.log("Client is initialized");
            ctx.setClient(client);
            saveClientState(client);
            return true;
          } else {
            dispatchPerunError(<p>{t("CouldNotInitialize")}</p>);
            setIsConnecting(false);
            return false;
          }
        })
        .catch(() => {
          dispatchPerunError(<p>{t("WSNotConnected")}</p>);
          setIsConnecting(false);
          return false;
        });
      if (isSuccess) {
        props.toggleOnboarded();
        setIsConnecting(false);
      }
    } catch (err) {
      console.log("Error creating client: ", err);
      dispatchPerunError(<p>{t("errorCreatingClient")}</p>);
      setIsConnecting(false);
      return;
    }
  };

  return (
    <>
      <div className={"justify-content-center Onboarding"}>
        <div className={"container"}>
          <div className={"left-div"}>
            {ethereumSelected && mmProvider ? (
              <div
                id={"eth-network"}
                className={"network"}
                style={{ backgroundColor: "rgb(212,213,214)" }}
              >
                <div
                  className={"close-button"}
                  onClick={() => setEthereumSelected(false)}
                >
                  <BiX size={20} />
                </div>
                <img src={ethlogo} alt="Ethereum logo" className="logo" />
                <h3>{ethNetworkName}</h3>
                <div className={"asset-display-left"}>
                  <div className={"circle center"}>ETH</div>
                </div>
                <ChainsList
                  onProcessChange={handleProcessChange}
                  onConnected={handleConnection}
                  externalProvider={mmProvider}
                  networks={config.networkIDs}
                />
              </div>
            ) : (
              <div>
                <br />
                <br />
                <div
                  className={`select-circle eth`}
                  onClick={() => {
                    setEthereumSelected(true);
                    setConnection(false);
                    ensureMMOnce().catch((e) => {
                      console.error(e);
                      dispatchPerunError(<p>{String(e.message || e)}</p>);
                    });
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <img className="logo" src={ethlogo} alt="" />
                  <div className="label">Ethereum</div>
                </div>
              </div>
            )}
          </div>
          <div className={"logo-column"}>
            <br />
            <br />
            <Row className={"logo justify-content-center"}>
              <img id="perun-logo-large" src={logo} alt={""} />
            </Row>
            <Button
              className={"my-auto"}
              disabled={
                isConnecting || (!connection && address === undefined)
              }
              variant="dark"
              onClick={async () => {
                let ethProvider: Provider | undefined;
                let nervosProvider: CkbCCCProvider | undefined;
                console.log("connecting to Metamask: ", connection);
                if (ethereumSelected || nervosSelected) {
                  try {
                    if (ethereumSelected && mmProvider && mmAccount) {
                      ethProvider = new Web3Provider(mmProvider, mmAccount);
                    } else {
                      console.log("Onboarding MM...");
                      const { provider: mm, account } = await ensureMMOnce();
                      ethProvider = new Web3Provider(mm, account);
                      setMmProvider(mm);
                      setMmAccount(account);
                    }
                  } catch (err) {
                    if (err instanceof Error) {
                      dispatchPerunError(<p>{err.message}</p>);
                    } else {
                      dispatchPerunError(<p>{t("errorOnboarding")}</p>);
                    }
                    return;
                  }
                }
                if (nervosSelected) {
                  const client: ClientJsonRpc = newCKBClient();

                  if (!signer) {
                    open(); // opens CCC wallet UI once
                    return;
                  }
                  if (!mmAccount) {
                    console.log("Metamask address not found");
                    return;
                  }
                  nervosProvider = new CkbCCCProvider(
                    signer,
                    client,
                    mmAccount,
                  );
                }
                initClient(ethProvider, undefined, nervosProvider);
              }}
            >
              {t("onboard")}
            </Button>
          </div>
          <div className={"right-div"}>
            {nervosSelected ? (
              <div
                id={"ste-network"}
                className={"network"}
                style={{ backgroundColor: "rgb(253,253,250)" }}
              >
                <div
                  className={"close-button"}
                  onClick={() => setNervosSelected(false)}
                >
                  <BiX size={20} />
                </div>
                <img src={ckblogo} alt="Nervos logo" className="logo" />
                <h3>Nervos {ckbNetworkName}</h3>
                <div className={"asset-display-left"}>
                  <div className={"circle center"}>CKB</div>
                </div>
                <div className={"stellarButton"}>
                  <Button
                    variant={"secondary"}
                    style={{
                      backgroundColor:
                        address !== undefined ? "#6b747c" : "#6b747c",
                      color: "white",
                      opacity: address !== undefined ? 0.6 : 1,
                    }}
                    onClick={async () => open()}
                  >
                    {address !== undefined
                      ? t("showaddress")
                      : `${t("connectto")} CKB`}
                    {address !== undefined ? (
                      <img
                        className={"conn-symbol"}
                        src={connected}
                        width={"20"}
                        alt={"connected"}
                      />
                    ) : (
                      <img
                        className={"conn-symbol"}
                        src={notConnected}
                        width={"20"}
                        alt={"not connected"}
                      />
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className={"select-circle nervos"}
                onClick={async () => {
                  const ok = await checkCkbAvailability();
                  if (!ok) {
                    // RPC is down → show a nice error and do NOT enable Nervos
                    dispatchPerunError(
                      <p>
                        {t("errorCkbUnavailable") ||
                          "The CKB RPC is currently unavailable (Cloudflare / node error). Nervos is temporarily disabled."}
                      </p>,
                    );
                    return;
                  }
                  setNervosSelected(true);
                  ensureMMOnce().catch((e) => {
                    console.error(e);
                    dispatchPerunError(<p>{String(e.message || e)}</p>);
                  });
                }}
                role="button"
                tabIndex={0}
              >
                <img className="logo" src={ckblogo} alt="" />
                <div className="label">Nervos</div>
              </div>
            )}
          </div>

          {/* SVG for lines */}
          <svg className="svg-lines">
            {connection && ethereumSelected && (
              <line x1="13%" y1="55%" x2="47%" y2="55%" className="line" />
            )}
            {address !== undefined && nervosSelected && (
              <line x1="87%" y1="55%" x2="53%" y2="55%" className="line" />
            )}
          </svg>
        </div>
      </div>
    </>
  );
}
